import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FiArrowLeft,
  FiSettings,
  FiServer,
  FiPlay,
  FiCheck,
  FiZap,
  FiPlus,
  FiDownload,
  FiMessageSquare,
  FiGrid,
  FiCpu,
  FiLayers,
  FiTool,
  FiPower
} from 'react-icons/fi';

const MCPGuide = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const steps = [
    {
      step: 1,
      title: 'Navigate to Settings/MCP Servers',
      description: 'Access the MCP server configuration from your Bervin settings.',
      icon: FiSettings,
      image: '/screenshots/mcp-step1-navigate-settings.png',
      details: [
        'Click on Settings in your Bervin dashboard',
        'Navigate to the "MCP Servers" section',
        'This is where you can manage all your MCP integrations',
        'You\'ll see the MCP server management interface'
      ]
    },
    {
      step: 2,
      title: 'Browse and Add MCP Servers',
      description: 'Choose from available MCP servers or add your own custom ones.',
      icon: FiServer,
      image: '/screenshots/mcp-step2-browse-add-servers.png',
      details: [
        'Browse through the list of available MCP servers',
        'Each server provides different tools and capabilities',
        'Click "Install" on any pre-configured MCP server',
        'Or click "Add Custom MCP" to add your own server',
        'Enter the server details for custom MCPs'
      ],
      highlight: true
    },
    {
      step: 3,
      title: 'Activate Installed MCP Servers',
      description: 'Go to the installed tab and activate your MCP servers for use in chat.',
      icon: FiPlay,
      image: '/screenshots/mcp-step3-activate-servers.png',
      details: [
        'Click on the "Installed" tab to see your MCP servers',
        'Find the MCP server you want to activate',
        'Click the play button (▶) next to the server name',
        'The server will become active and ready for use'
      ]
    }
  ];

  const additionalInfo = {
    title: 'Quick Access to Running Servers',
    description: 'Easily manage your active MCP servers directly from the chat interface.',
    icon: FiMessageSquare,
    image: '/screenshots/mcp-quick-access-chat.png',
    details: [
      'Look for the MCP icon in your chat input area',
      'Click the MCP icon to see all running servers',
      'Toggle servers on/off without leaving the chat',
      'See which tools are available from each server',
      'Quickly switch between different MCP capabilities'
    ]
  };

  const benefits = [
    {
      icon: FiTool,
      title: 'Extended Capabilities',
      description: 'Add powerful tools and integrations to enhance your AI conversations.'
    },
    {
      icon: FiLayers,
      title: 'Multiple Protocols',
      description: 'Run multiple MCP servers simultaneously for diverse functionality.'
    },
    {
      icon: FiZap,
      title: 'Real-time Integration',
      description: 'Access external services and data in real-time during conversations.'
    },
    {
      icon: FiCpu,
      title: 'Custom Solutions',
      description: 'Add your own custom MCP servers for specialized use cases.'
    }
  ];

  const popularMCPs = [
    {
      name: 'File System',
      description: 'Read, write, and manage files on your local system',
      icon: '📁'
    },
    {
      name: 'Web Search',
      description: 'Search the web and get real-time information',
      icon: '🔍'
    },
    {
      name: 'Database',
      description: 'Connect to and query databases directly',
      icon: '🗄️'
    },
    {
      name: 'Git Integration',
      description: 'Manage Git repositories and version control',
      icon: '📝'
    },
    {
      name: 'API Client',
      description: 'Make HTTP requests to external APIs',
      icon: '🌐'
    },
    {
      name: 'Calendar',
      description: 'Access and manage your calendar events',
      icon: '📅'
    }
  ];

  const tips = [
    {
      title: 'Start with Essential MCPs',
      description: 'Begin with file system and web search MCPs for general productivity.'
    },
    {
      title: 'Monitor Resource Usage',
      description: 'Keep an eye on system resources when running multiple MCP servers.'
    },
    {
      title: 'Custom MCP Development',
      description: 'Create your own MCP servers for specialized business needs.'
    },
    {
      title: 'Security Considerations',
      description: 'Only install MCP servers from trusted sources to maintain security.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Header />
      
      <div className="pt-20">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link 
            to="/guide" 
            className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors"
          >
            <FiArrowLeft className="w-4 h-4 mr-2" />
            Back to Guide
          </Link>
          <div className="text-gray-400 mt-2">
            <span>Guide</span> / <span className="text-purple-400">MCP Server Setup</span>
          </div>
        </div>

        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-2xl mb-8">
              <FiServer className="w-10 h-10 text-white" />
            </div>
            
            <h1 className="text-4xl md:text-6xl font-display font-bold mb-6 gradient-text">
              MCP Server Setup Guide
            </h1>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Learn how to set up and use Model Context Protocol (MCP) servers to extend 
              your AI's capabilities with powerful tools and integrations.
            </p>

            <div className="flex items-center justify-center space-x-6 mt-8">
              <div className="flex items-center space-x-2 text-gray-400">
                <FiServer className="w-5 h-5" />
                <span>Multiple Servers</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400">
                <FiTool className="w-5 h-5" />
                <span>Extended Tools</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400">
                <FiZap className="w-5 h-5" />
                <span>Real-time Integration</span>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Step-by-Step Guide */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 gradient-text">
              Setup Process
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Follow these simple steps to set up and activate MCP servers in Bervin
            </p>
          </motion.div>

          <div className="space-y-16">
            {steps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative ${step.highlight ? 'bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 rounded-2xl p-8' : ''}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="flex items-start space-x-4">
                      <div className={`flex-shrink-0 w-12 h-12 ${step.highlight ? 'bg-gradient-to-r from-purple-500 to-cyan-500' : 'bg-gray-700'} rounded-xl flex items-center justify-center text-white font-bold text-lg`}>
                        {step.step}
                      </div>
                      <div className="flex-1">
                        <h3 className={`text-2xl font-bold mb-4 ${step.highlight ? 'text-purple-300' : 'text-white'}`}>
                          {step.title}
                        </h3>
                        <p className="text-gray-300 text-lg mb-6">
                          {step.description}
                        </p>
                        <ul className="space-y-2">
                          {step.details.map((detail, detailIndex) => (
                            <li key={detailIndex} className="flex items-start space-x-3">
                              <FiCheck className={`w-5 h-5 ${step.highlight ? 'text-purple-400' : 'text-green-400'} flex-shrink-0 mt-0.5`} />
                              <span className="text-gray-300">{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''} ${step.highlight ? 'relative' : ''}`}>
                    {step.highlight && (
                      <div className="absolute -inset-2 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-2xl opacity-20 animate-pulse"></div>
                    )}
                    <div className={`${step.highlight ? 'relative' : ''} bg-gray-800/50 rounded-xl p-8 border border-gray-700`}>
                      <div className="text-center">
                        <img 
                          src={step.image} 
                          alt={`${step.title} - Step ${step.step}`}
                          className="w-full max-w-md mx-auto rounded-lg border border-gray-600 shadow-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div className="text-center text-gray-400 hidden">
                          <step.icon className="w-16 h-16 mx-auto mb-4 opacity-70" />
                          <p className="text-lg font-medium mb-2">Step {step.step}: {step.title}</p>
                          <p className="text-sm">Screenshot placeholder - Add your image to: {step.image}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Additional Info Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 rounded-2xl p-8"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-xl flex items-center justify-center">
                    <additionalInfo.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-cyan-300">
                    {additionalInfo.title}
                  </h3>
                </div>
                <p className="text-gray-300 text-lg mb-6">
                  {additionalInfo.description}
                </p>
                <ul className="space-y-2">
                  {additionalInfo.details.map((detail, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <FiCheck className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-gray-800/50 rounded-xl p-8 border border-gray-700">
                <div className="text-center">
                  <img 
                    src={additionalInfo.image} 
                    alt="Quick Access to MCP Servers from Chat"
                    className="w-full max-w-md mx-auto rounded-lg border border-gray-600 shadow-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div className="bg-gray-700 rounded-lg p-4 mb-4 hidden">
                    <div className="flex items-center justify-center space-x-2 mb-2">
                      <FiMessageSquare className="w-5 h-5 text-cyan-400" />
                      <span className="text-white font-medium">Chat Input Area</span>
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <div className="bg-gray-600 px-3 py-1 rounded text-sm">Type your message...</div>
                      <div className="bg-gradient-to-r from-cyan-500 to-purple-500 p-2 rounded-lg cursor-pointer hover:from-cyan-600 hover:to-purple-600 transition-all">
                        <FiGrid className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  </div>
                  <p className="text-cyan-300 font-medium text-sm mt-4">
                    💡 Click the MCP icon for quick access to running servers
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Popular MCPs Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 text-white">
              Popular MCP Servers
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Explore some of the most useful MCP servers available for Bervin
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularMCPs.map((mcp, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-800/50 backdrop-blur border border-gray-700 rounded-xl p-6 hover:border-purple-500/50 transition-all duration-300"
              >
                <div className="text-center">
                  <div className="text-3xl mb-3">{mcp.icon}</div>
                  <h3 className="text-lg font-bold text-white mb-2">{mcp.name}</h3>
                  <p className="text-gray-300 text-sm">{mcp.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Benefits Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 gradient-text">
              Why Use MCP Servers?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Discover the powerful benefits of extending your AI with MCP integrations
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-800/50 backdrop-blur border border-gray-700 rounded-xl p-6 hover:border-purple-500/50 transition-all duration-300 text-center"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <benefit.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-gray-300 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Tips Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 text-white">
              Pro Tips for MCP Management
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {tips.map((tip, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 rounded-xl p-6"
              >
                <h3 className="text-lg font-semibold text-purple-300 mb-2">{tip.title}</h3>
                <p className="text-gray-300">{tip.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-purple-500/30 rounded-2xl p-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 gradient-text">
              Ready to Extend Your AI?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Start setting up MCP servers and unlock powerful new capabilities for your AI conversations.
            </p>
            <a
              href="https://bervin.appiworks.com/app"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105"
            >
              <FiPower className="w-5 h-5 mr-2" />
              Start Using MCPs
            </a>
          </motion.div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default MCPGuide;
